import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useSnackbar } from "@/hooks/useSnackbar";
import {
  GROUP_VISIT_DETAIL_QUERY_KEY,
  useGroupVisitById,
} from "@/queries/group-visit/use-group-visit-by-id";
import { GROUP_VISIT_LIST_QUERY_KEY } from "@/queries/group-visit/use-group-visit-list";
import { useUpdateGroupVisit } from "@/queries/group-visit/use-update-group-visit";
import { getErrorSnackbar, getSuccessSnackbar } from "@/utils/utils";
import { NOTIFICATION_SETTINGS_INITIAL_VALUES } from "../group-visit-constant";
import {
  GroupNotificationSettings,
  NotificationSection,
} from "../group-visit-types";
import {
  getNotificationField,
  toGroupNotificationSettings,
} from "../group-visit-utils";

export type UseGroupNotificationSettings = {
  settings: GroupNotificationSettings;
  isLoading: boolean;
  /** Persists one toggle, then re-reads the group so the UI shows what was saved. */
  updateSetting: (
    section: NotificationSection,
    id: string,
    checked: boolean
  ) => Promise<void>;
};

/**
 * Notifications & Reminders for a group, read from and written back to the
 * group record one toggle at a time.
 *
 * @author Rajkumar Rathod
 */
export const useGroupNotificationSettings = (
  groupVisitId?: string
): UseGroupNotificationSettings => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  const { data: savedGroup, isLoading } = useGroupVisitById(groupVisitId);
  const { mutateAsync: updateGroupVisit } = useUpdateGroupVisit();

  const settings = useMemo(
    () =>
      savedGroup
        ? toGroupNotificationSettings(savedGroup)
        : NOTIFICATION_SETTINGS_INITIAL_VALUES,
    [savedGroup]
  );

  // The update rewrites every field, so the saved record travels with the toggle.
  const updateSetting = useCallback(
    async (section: NotificationSection, id: string, checked: boolean) => {
      const field = getNotificationField(section, id);
      if (!savedGroup || !field) return;

      try {
        const res = await updateGroupVisit({
          groupVisit: { ...savedGroup, [field]: checked },
        });

        await queryClient.invalidateQueries({
          queryKey: [GROUP_VISIT_DETAIL_QUERY_KEY, groupVisitId],
        });
        queryClient.invalidateQueries({
          queryKey: [GROUP_VISIT_LIST_QUERY_KEY],
        });
        showSnackbar(getSuccessSnackbar(res));
      } catch (error) {
        showSnackbar(getErrorSnackbar(error));
      }
    },
    [savedGroup, groupVisitId, updateGroupVisit, queryClient, showSnackbar]
  );

  return { settings, isLoading, updateSetting };
};
